/**
 * Utility functions for handling asset URLs
 */

/**
 * Converts a relative asset path to a full URL with the assets endpoint prefix
 * @param relativePath - The relative path from the database
 * @returns Full URL to the asset, or undefined if no path provided
 */
export function getAssetUrl<TPath extends string | undefined>(
  relativePath: TPath,
): TPath extends string ? string : undefined {
  if (!relativePath) {
    return undefined as TPath extends string ? string : undefined;
  }

  if (relativePath.startsWith(`${window.location.origin}/assets/`)) {
    return relativePath.toString() as TPath extends string ? string : undefined;
  }

  // Remove leading slash if present to avoid double slashes
  const cleanPath = relativePath.startsWith("/") ? relativePath.slice(1) : relativePath;

  return `${window.location.origin}/assets/${cleanPath}` as TPath extends string ? string : undefined;
}

/**
 * Normalizes an already-resolved asset URL to the current origin.
 *
 * Some server responses (e.g. chat attachments) embed an absolute `/assets/` URL
 * built against a fixed endpoint. That URL is correct in production but points at
 * the wrong host in other environments. Re-resolving it through `getAssetUrl`
 * keeps the path but swaps in `window.location.origin`, so the dev `/assets/`
 * proxy serves it — matching how avatars (raw paths) already resolve.
 */
export function getAssetUrlFromResolved(resolvedUrl: string): string {
  const marker = "/assets/";
  const idx = resolvedUrl.indexOf(marker);
  const path = idx >= 0 ? resolvedUrl.slice(idx + marker.length) : resolvedUrl;
  return getAssetUrl(path);
}
